import { FormUnit } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { useLanguage } from "src/context/LanguageProvider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ScoreImprovement } from "src/types";
import { formatNumberByType } from "src/utils/formatting";

type ComparisonValue = {
    start: string;
    end: string;
    change: string | null;
    unit: string;
    fieldType?: string;
};

type Props = {
    score: number | undefined | null;
    value: ComparisonValue;
    scoreImprovement?: ScoreImprovement;
    scorePrecision?: number;
    scoreUnit?: string;
};
export const ImpactComparison = (props: Props) => {
    const { score, value } = props;
    const theme = useTheme();
    const { lastSelectedVariation, selectedScorePrecision, getColor, formatScore } = useEntityScores();
    const { currentLanguage } = useLanguage();
    const {
        scoreImprovement = lastSelectedVariation.improvement,
        scorePrecision = selectedScorePrecision,
        scoreUnit = lastSelectedVariation.unit,
    } = props;

    const canShowUnit = (value.unit === "%" || value.unit === "percent") === false;

    const difference = formatDifference(value);

    const color = getColor(score, { improvement: scoreImprovement, precision: scorePrecision });

    const v = formatScore(score, { precision: scorePrecision });
    return (
        <Stack flexDirection={"row"} gap={theme.spacing(1.5)}>
            {difference !== null && difference !== 0 && canShowUnit && (
                <Stack flexDirection="row" gap={theme.spacing(0.5)}>
                    <Typography variant="body" alignContent={"center"}>
                        {difference > 0 ? "+" : ""}
                        {formatNumberByType(difference, value.unit, currentLanguage, lastSelectedVariation.precision)}
                    </Typography>
                    <Typography variant="body" alignContent={"center"}>
                        {value.unit}
                    </Typography>
                </Stack>
            )}
            {v !== null && (
                <ApproximateImpactTooltip value={score} precision={scorePrecision} unit={scoreUnit}>
                    <FormUnit value={v} unit={scoreUnit} color={color} />
                </ApproximateImpactTooltip>
            )}
        </Stack>
    );
};

export const formatDifference = (value: ComparisonValue): number | null => {
    const { start, end, change, fieldType = "number" } = value;

    if (change !== undefined && change !== null && change !== "") {
        return checkNumber(change);
    }

    if (fieldType !== "number") {
        return null;
    }

    const startValue = checkNumber(start);
    const endValue = checkNumber(end);

    if (startValue === null && endValue === null) {
        return null;
    }

    if (startValue === null) {
        return endValue;
    }

    if (endValue === null) {
        return startValue;
    }

    const difference = endValue - startValue;
    return difference;
};

const checkNumber = (value: string | undefined | null): number | null => {
    if (!value) return null;

    const numberValue = Number(value);
    if (isNaN(numberValue)) {
        return null;
    }

    return numberValue;
};
